import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import Callout from '../../Common/Callout';
import InfoPopover from '../../Common/InfoPopover';
import '../Personell/employee-form.css';
import './payslip.css';
import DatePicker from 'react-datepicker';
import { Save, Trash2, Calculator } from 'lucide-react';
import { COMPREHENSIVE_WAGE_TYPES, getAbsenceWageTypes } from './wageTypesConfig';
import * as payrollApi from '../../../../services/api/payrollApi';
import "react-datepicker/dist/react-datepicker.css";

// Use comprehensive wage types configuration
const ABSENCE_WAGE_TYPES = getAbsenceWageTypes();
const ESSENTIAL_WAGE_TYPES = COMPREHENSIVE_WAGE_TYPES;

// Wage codes that draw from the vacation balance (vacation_balances.used_days).
// Mirror of payrolls/services/VacationBalanceService.VACATION_WAGE_CODES on
// the backend — when the admin is editing one of these rows we fetch the
// employee's current snapshot so they can see remaining days before saving.
const VACATION_WAGE_CODES = new Set(['3220', '3221', '3232', '3235']);

const EditPayslipRowModal = ({ show, handleClose, item, contract, handleEdit, handleDelete, companyId, employeeId }) => {
    const [editedItem, setEditedItem] = useState(item);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showDateFields, setShowDateFields] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [vacationSnapshot, setVacationSnapshot] = useState(null);

    const isVacationRow = !!editedItem?.wageType?.code && VACATION_WAGE_CODES.has(editedItem.wageType.code);

    // Fetch vacation snapshot when modal opens on a vacation row, so the
    // admin sees "X dagar kvar" before changing the quantity. Avoids the
    // common foot-gun of pushing the employee into förskott without
    // realising it.
    useEffect(() => {
        if (!show || !isVacationRow || !companyId || !employeeId) {
            setVacationSnapshot(null);
            return;
        }
        let cancelled = false;
        payrollApi.getVacationBalance(companyId, { employeeId })
            .then(res => {
                if (cancelled) return;
                const snap = res?.data?.balances?.[0]
                    || res?.data?.balance
                    || res?.data
                    || null;
                setVacationSnapshot(snap);
            })
            .catch(() => { if (!cancelled) setVacationSnapshot(null); });
        return () => { cancelled = true; };
    }, [show, isVacationRow, companyId, employeeId]);

    // Helper function to get input configuration for a wage type
    const getInputConfig = (wageTypeCode) => {
        return ESSENTIAL_WAGE_TYPES[wageTypeCode] || {
            unit: null,
            inputType: 'hours',
            defaultQuantity: 1,
            label: 'Antal',
            requiresAbsenceDates: false,
            icon: Calculator,
            color: '#6b7280',
            description: 'Standard löneart'
        };
    };

    // Validation function
    const validateForm = () => {
        const errors = [];

        if (!editedItem?.quantity || editedItem.quantity <= 0) {
            errors.push('Antal måste vara större än 0');
        }

        // NOTE: Rate is calculated from contract, not validated here

        if (showDateFields) {
            if (!editedItem?.absenceStartDate || !editedItem?.absenceEndDate) {
                const config = getInputConfig(editedItem?.wageType?.code);
                errors.push(`${config.absenceType} kräver frånvarodatum enligt svensk lag`);
            } else if (new Date(editedItem.absenceStartDate) > new Date(editedItem.absenceEndDate)) {
                errors.push('Startdatum kan inte vara efter slutdatum');
            }
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    useEffect(() => {
        setEditedItem(item);

        // Check if this wage type requires absence dates
        const requiresAbsenceDates = item?.wageType?.code &&
            ABSENCE_WAGE_TYPES.includes(item.wageType.code);

        setShowDateFields(requiresAbsenceDates);

        // Initialize dates if they exist in the item
        if (item?.absenceStartDate) {
            setStartDate(new Date(item.absenceStartDate));
        }
        if (item?.absenceEndDate) {
            setEndDate(new Date(item.absenceEndDate));
        }
    }, [item]);

    const handleInputChange = (event) => {
        setEditedItem({ ...editedItem, [event.target.name]: event.target.value });
    };

    // Handle date changes
    const handleStartDateChange = (date) => {
        setStartDate(date);
        setEditedItem({ ...editedItem, absenceStartDate: date ? date.toISOString().split('T')[0] : null });
    };

    const handleEndDateChange = (date) => {
        setEndDate(date);
        setEditedItem({ ...editedItem, absenceEndDate: date ? date.toISOString().split('T')[0] : null });
    };

    // Save changes with validation for required fields
    const saveChanges = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSaving(true);

        try {
            // IMPORTANT: Only send quantity and absence dates
            // Backend will recalculate amount and rate from the employee contract
            // This ensures contract rates are the single source of truth
            const updatedItem = {
                ...editedItem,
                // DO NOT send amount or rate - backend calculates from contract
            };

            console.log('Saving changes (backend will recalculate amount/rate from contract):', updatedItem);
            await handleEdit(updatedItem);
            setValidationErrors([]);
        } catch (error) {
            console.error('Error saving changes:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>

            <Modal show={show} onHide={handleClose} className="emp-modal" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Ändra lönerad</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {editedItem && (() => {
                        const config = getInputConfig(editedItem.wageType.code);
                        const total = Math.round((Number(editedItem.quantity) || 0) * (Number(editedItem.rate) || 0));
                        return (
                            <>
                                <div className="current-value-card">
                                    <div className="current-value-content">
                                        <div className="current-value-label">Löneart {editedItem.wageType.code}</div>
                                        <div className="current-value-text" style={{ fontWeight: 600 }}>{editedItem.wageType.description}</div>
                                        {config.description && (
                                            <div className="text-muted" style={{ fontSize: '0.8125rem', marginTop: '0.125rem' }}>{config.description}</div>
                                        )}
                                    </div>
                                </div>

                                {isVacationRow && vacationSnapshot && (() => {
                                    const earned = Number(vacationSnapshot.earnedDays ?? vacationSnapshot.earned_days ?? 0);
                                    const carried = Number(vacationSnapshot.carriedOverDays ?? vacationSnapshot.carried_over_days ?? 0);
                                    const saved = Number(vacationSnapshot.savedDays ?? vacationSnapshot.saved_days ?? 0);
                                    const used = Number(vacationSnapshot.usedDays ?? vacationSnapshot.used_days ?? 0);
                                    const remainingPaid = Math.max(0, earned + carried - used);
                                    const advanceTaken = Math.max(0, used - earned - carried);
                                    const requested = Number(editedItem?.quantity || 0);
                                    const willOverdraw = requested > remainingPaid;
                                    const warn = advanceTaken > 0 || willOverdraw;
                                    return (
                                        <Callout tone={warn ? 'warning' : 'success'}>
                                            <strong>Semestersaldo:</strong> {remainingPaid} betalda dagar kvar
                                            <span className="text-muted"> (tjänat {earned} + överförda {carried} − använt {used}{saved > 0 ? `, sparade ${saved}` : ''})</span>
                                            {advanceTaken > 0 && <><br />Förskottssemester: {advanceTaken} dagar.</>}
                                            {willOverdraw && advanceTaken === 0 && <><br />{requested - remainingPaid} dag(ar) av det här uttaget blir förskott.</>}
                                        </Callout>
                                    );
                                })()}

                                {validationErrors.length > 0 && (
                                    <div className="validation-summary" role="alert">
                                        <div className="validation-content">
                                            <ul className="validation-list">
                                                {validationErrors.map((error, index) => <li key={index} className="validation-item">{error}</li>)}
                                            </ul>
                                        </div>
                                    </div>
                                )}

                                <div className="form-section">
                                    <div className="form-grid">
                                        <div className="input-group">
                                            <label className="input-label" htmlFor="payslip-row-quantity">
                                                {config.label}
                                                <span className="required-indicator">*</span>
                                            </label>
                                            <div className="input-container">
                                                <input
                                                    id="payslip-row-quantity"
                                                    type="number"
                                                    className="modern-input"
                                                    name="quantity"
                                                    min="0"
                                                    step={config.inputType === 'amount' ? '0.01' : '1'}
                                                    value={editedItem.quantity ?? ''}
                                                    onChange={handleInputChange}
                                                    autoFocus
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label className="input-label" htmlFor="payslip-row-rate">
                                                À-pris
                                                <InfoPopover ariaLabel="Om à-pris">
                                                    Beloppet per enhet hämtas från anställningsavtalet och räknas om av systemet när raden sparas. Det kan inte ändras här.
                                                </InfoPopover>
                                            </label>
                                            <div className="input-container">
                                                <input
                                                    id="payslip-row-rate"
                                                    type="text"
                                                    className="modern-input"
                                                    value={editedItem.rate != null ? `${Number(editedItem.rate).toLocaleString('sv-SE')} kr` : 'Beräknas från avtal'}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        {showDateFields && (
                                            <div className="input-group">
                                                <label className="input-label" htmlFor="payslip-row-absence">
                                                    Frånvarodatum
                                                    <span className="required-indicator">*</span>
                                                    {config.absenceType && (
                                                        <InfoPopover ariaLabel="Om frånvarodatum">
                                                            Frånvarodatum krävs för {config.absenceType} när uppgiften lämnas till Skatteverket.
                                                        </InfoPopover>
                                                    )}
                                                </label>
                                                <div className="input-container">
                                                    <DatePicker
                                                        id="payslip-row-absence"
                                                        selected={startDate}
                                                        onChange={handleStartDateChange}
                                                        className="modern-input"
                                                        dateFormat="yyyy-MM-dd"
                                                        placeholderText="Välj datum"
                                                        portalId="ll-datepicker-portal"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="slip__sum is-net" style={{ marginTop: '1rem' }}>
                                    <span className="slip__sum-label">Summa</span>
                                    <span>{total.toLocaleString('sv-SE')} kr</span>
                                </div>
                            </>
                        );
                    })()}
                </Modal.Body>

                <Modal.Footer className="justify-content-between">
                    <button type="button" className="ll-btn ll-btn--danger" onClick={() => handleDelete(item)}>
                        <Trash2 />
                        Ta bort rad
                    </button>
                    <div className="d-flex gap-2">
                        <button type="button" className="ll-btn ll-btn--secondary" onClick={handleClose}>
                            Avbryt
                        </button>
                        <button type="button" className="ll-btn ll-btn--primary" onClick={saveChanges} disabled={isSaving}>
                            {isSaving ? <Spinner animation="border" size="sm" /> : <Save />}
                            Spara
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditPayslipRowModal;